import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Reveal } from "./Reveal";
import { SevenPointStar } from "./Star";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [10, 25, 50, 100];

export default function Donate() {
  const [selected, setSelected] = useState(25);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  const amount = custom !== "" ? parseFloat(custom) : selected;
  const valid = Number.isFinite(amount) && amount >= 1 && amount <= 50000;

  const donate = async () => {
    if (!valid) {
      toast.error("Enter a donation between $1 and $50,000 AUD");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        amount,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the donation — please try again");
      setLoading(false);
    }
  };

  return (
    <section id="donate" data-testid="donate-section" className="border-t border-jordan-ink/15">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-5 py-24 sm:px-10 sm:py-36 lg:grid-cols-2">
        <Reveal>
          <p className="mb-4 font-display text-xs font-bold uppercase tracking-[0.3em] text-jordan-red">
            Give · الكرم
          </p>
          <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl">
            Fuel the<br />
            <span className="text-jordan-red">madafa</span> fire
          </h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-jordan-mute sm:text-lg">
            In Jordanian culture, the madafa — the guest house — never turns anyone away.
            Your donation keeps our doors open: funding events, cultural programs, support
            for families and charitable causes close to our hearts.
          </p>
          <div className="mt-10 flex items-center gap-3 text-jordan-green">
            <SevenPointStar className="h-5 w-5" />
            <span className="font-arabic text-2xl">الكرم من شيمنا</span>
            <span className="font-serifx text-lg italic text-jordan-mute">— generosity is our nature</span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-jordan-ink/20 bg-white/60 p-8 sm:p-12" data-testid="donation-card">
            <p className="mb-6 font-display text-xs font-bold uppercase tracking-[0.25em] text-jordan-mute">
              Choose an amount (AUD)
            </p>
            <div className="grid grid-cols-2 gap-px border border-jordan-ink/20 bg-jordan-ink/20 sm:grid-cols-4">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`donate-preset-${p}`}
                  onClick={() => { setSelected(p); setCustom(""); }}
                  className={`bg-white px-4 py-5 font-display text-2xl font-extrabold transition-colors duration-300 ${
                    custom === "" && selected === p
                      ? "!bg-jordan-ink text-jordan-sand"
                      : "hover:bg-jordan-sand"
                  }`}
                >
                  ${p}
                </button>
              ))}
            </div>
            <div className="mt-6 flex items-center border border-jordan-ink/20 bg-white focus-within:border-jordan-red">
              <span className="pl-5 font-display text-xl font-bold text-jordan-mute">$</span>
              <input
                type="number"
                min="1"
                max="50000"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Custom amount"
                data-testid="donate-custom-input"
                className="w-full bg-transparent px-3 py-5 font-display text-xl font-bold outline-none placeholder:font-normal placeholder:text-jordan-mute/60"
              />
              <span className="pr-5 font-display text-xs font-bold uppercase tracking-widest text-jordan-mute">AUD</span>
            </div>
            <button
              type="button"
              onClick={donate}
              disabled={loading || !valid}
              data-testid="donate-submit-btn"
              className="mt-8 w-full border border-jordan-red bg-jordan-red py-5 font-display text-sm font-black uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-jordan-ink hover:border-jordan-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Opening secure checkout…" : `Donate ${valid ? `$${amount}` : ""} now`}
            </button>
            <p className="mt-5 flex items-center justify-center gap-2 text-xs text-jordan-mute" data-testid="donate-secure-note">
              <Lock className="h-3.5 w-3.5" /> Secure one-time payment via Stripe · cards & Apple Pay
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
